import { NgFor, NgIf } from '@angular/common';
import { Component, inject } from '@angular/core';
import { MatCheckboxChange, MatCheckbox } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  MatExpansionPanel,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle,
  MatExpansionPanelDescription,
} from '@angular/material/expansion';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatSelectChange, MatSelect, MatOption } from '@angular/material/select';
import { RootState } from '@next/core-lib/app';
import {
  AbstractDialogData,
  AbstractDialog,
  AbstractDialogResultData,
  DialogComponent,
} from '@next/core-lib/dialog';
import {
  LocaleService,
  CurrencySymbolPipe,
  TranslatePipe,
  AgrDatePipe,
  AgrNumberPipe,
} from '@next/core-lib/i18n';
import { Store } from '@ngrx/store';

import { Barn } from '../../models/cycle.model';
import {
  SlaughterReport,
  Cycle,
  LinkedSlaughterReport,
  SlaughterReportBarnLink,
  MessageTypeCodeEnum,
} from '../../models/flock-registration-service.model';
import * as cycleActions from '../../store/actions/cycle.actions';

export interface ImportFromSlaughterReportDialogInputData extends AbstractDialogData {
  slaughterReports: SlaughterReport[];
  cycle: Cycle;
}

export interface ImportFromSlaughterReportDialogResultData {
  linkedSlaughterReports: LinkedSlaughterReport[];
}

@Component({
  selector: 'app-import-from-slaughter-report',
  templateUrl: 'import-from-slaughter-report.component.html',
  styleUrls: ['import-from-slaughter-report.component.scss'],
  imports: [
    DialogComponent,
    NgFor,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    MatCheckbox,
    MatExpansionPanelDescription,
    NgIf,
    MatFormField,
    MatLabel,
    MatSelect,
    MatOption,
    CurrencySymbolPipe,
    TranslatePipe,
    AgrDatePipe,
    AgrNumberPipe,
  ],
})
export class ImportFromSlaughterReportComponent extends AbstractDialog<ImportFromSlaughterReportDialogResultData> {
  data = inject<ImportFromSlaughterReportDialogInputData>(MAT_DIALOG_DATA);
  protected mdDialogRef =
    inject<
      MatDialogRef<
        ImportFromSlaughterReportComponent,
        AbstractDialogResultData<ImportFromSlaughterReportDialogResultData>
      >
    >(MatDialogRef);
  private readonly store = inject<Store<RootState>>(Store);
  private readonly localeService = inject(LocaleService);

  barns: Barn[] = [];
  showFinancialData: Record<number, boolean> = {};

  currentLinks: SlaughterReportBarn[] = [];
  afterPaymentTypeCode: MessageTypeCodeEnum.AfterPayment;

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {
    super();

    this.data.confirmButton = true;
    // Update text when a checkbox is checked/unchecked.
    this.data.confirmButtonText = `${this.localeService.translate('import')} (0)`;
    this.data.disableConfirmButton = true;
    this.data.title = this.localeService.translate('link-slaughter-results-title');
    const barns: Barn[] = [];
    this.data?.cycle?.dayOldChicks.forEach((d) => {
      if (barns.filter((b) => d.barn.id === b.id).length === 0) {
        barns.push(d.barn);
      }
    });
    this.barns = barns;
  }

  getDepartureType(slaughterReport: SlaughterReport): string {
    return slaughterReport.technicalResult.group.animalBatchClassificationCode === 1
      ? 'departure-item.type-partial'
      : 'departure-item.type-full';
  }

  save() {
    const validLinks: SlaughterReportBarnLink[] = this.currentLinks
      .filter((link) => link.barnId !== undefined && link.isEnabled)
      .map(
        (l) => <SlaughterReportBarnLink>(<unknown>{
            slaughterReportId: l.slaughterReportId,
            barnId: l.barnId,
          }),
      );
    if (validLinks.length > 0) {
      this.store.dispatch(
        cycleActions.linkSlaughterReportsRequest({
          cycleId: this.data.cycle.id,
          links: {
            barnSlaughterReports: validLinks,
          },
        }),
      );

      super.confirm();
    }
  }

  updateBarnSelection($event: MatSelectChange, reportId: number) {
    const current = this.currentLinks.find((c) => c.slaughterReportId === reportId);
    if (current) {
      current.barnId = $event.value.id;
    } else {
      this.currentLinks.push({
        barnId: $event.value.id,
        slaughterReportId: reportId,
        isEnabled: false,
      });
    }
    this.updateState();
  }

  updateReportSelection($event: MatCheckboxChange, reportId: number) {
    const current = this.currentLinks.find((c) => c.slaughterReportId === reportId);
    if (current) {
      current.isEnabled = $event.checked;
    } else {
      this.currentLinks.push({
        isEnabled: $event.checked,
        slaughterReportId: reportId,
      });
    }
    this.updateState();
  }

  updateShowFinancialData($event: MatCheckboxChange, reportId: number) {
    this.showFinancialData[reportId] = $event.checked;
  }

  updateState() {
    const validCount = this.currentLinks.reduce(
      (amount, link) => amount + (link.barnId !== undefined && link.isEnabled ? 1 : 0),
      0,
    );
    this.data.confirmButtonText = `${this.localeService.translate('import')} (${validCount})`;
    this.data.disableConfirmButton = validCount === 0;
  }

  public get messageTypeCode(): typeof MessageTypeCodeEnum {
    return MessageTypeCodeEnum;
  }
}

export interface SlaughterReportBarn {
  slaughterReportId: number;
  barnId?: number;
  isEnabled: boolean;
}
